import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useCompany } from './CompanyContext';
import {
  Container,
  Typography,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Paper,
  List,
  ListItem,
  ListItemText,
  Divider,
  Box,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

const KpiCard = ({ title, value, color }) => (
  <Card sx={{ backgroundColor: color, color: 'white' }}>
    <CardContent>
      <Typography variant="h6" component="div">
        {title}
      </Typography>
      <Typography variant="h3" component="div">
        {value}
      </Typography>
    </CardContent>
  </Card>
);

const GastoKpiCard = ({ value, color, selectedMonth, selectedYear, onMonthChange, onYearChange }) => {
  const months = [
    { value: 1, label: 'Ene' },
    { value: 2, label: 'Feb' },
    { value: 3, label: 'Mar' },
    { value: 4, label: 'Abr' },
    { value: 5, label: 'May' },
    { value: 6, label: 'Jun' },
    { value: 7, label: 'Jul' },
    { value: 8, label: 'Ago' },
    { value: 9, label: 'Sep' },
    { value: 10, label: 'Oct' },
    { value: 11, label: 'Nov' },
    { value: 12, label: 'Dic' }
  ];

  const currentYear = new Date().getFullYear();
  const years = [currentYear, currentYear - 1, currentYear - 2];

  return (
    <Card sx={{ backgroundColor: color, color: 'white' }}>
      <CardContent>
        <Typography variant="h6" component="div" sx={{ mb: 1 }}>
          Gasto
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5, mb: 1 }}>
          <Select
            value={selectedMonth}
            onChange={(e) => onMonthChange(e.target.value)}
            size="small"
            sx={{
              color: 'white',
              fontSize: '0.75rem',
              '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.5)' },
              '& .MuiSvgIcon-root': { color: 'white' },
              minWidth: '70px'
            }}
          >
            {months.map(month => (
              <MenuItem key={month.value} value={month.value} sx={{ fontSize: '0.75rem' }}>
                {month.label}
              </MenuItem>
            ))}
          </Select>
          <Select
            value={selectedYear}
            onChange={(e) => onYearChange(e.target.value)}
            size="small"
            sx={{
              color: 'white',
              fontSize: '0.75rem',
              '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.5)' },
              '& .MuiSvgIcon-root': { color: 'white' },
              minWidth: '75px'
            }}
          >
            {years.map(year => (
              <MenuItem key={year} value={year} sx={{ fontSize: '0.75rem' }}>
                {year}
              </MenuItem>
            ))}
          </Select>
        </Box>
        <Typography variant="h3" component="div">
          {value}
        </Typography>
      </CardContent>
    </Card>
  );
};

function Dashboard() {
  const [dashboardData, setDashboardData] = useState(null);
  const [ocTrafficLight, setOcTrafficLight] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { selectedCompanyIds } = useCompany();
  const navigate = useNavigate();

  // Month/Year selector state
  const currentDate = new Date();
  const [selectedMonth, setSelectedMonth] = useState(currentDate.getMonth() + 1); // 1-12
  const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear());

  // Helper function to categorize OCs by payment deadline
  const categorizeOCs = (ocs) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const categories = {
      green: { count: 0, label: '+ 15 días', color: '#EA8C55', ocs: [] },
      yellow: { count: 0, label: '15 días', color: '#C75146', ocs: [] },
      orange: { count: 0, label: '10 días', color: '#AD2E24', ocs: [] },
      red: { count: 0, label: '5 días', color: '#81171B', ocs: [] },
      black: { count: 0, label: 'Hoy', color: '#540804', ocs: [] }
    };

    ocs.forEach(oc => {
      if (!oc.fecha_limite_pago) return;

      const deadline = new Date(oc.fecha_limite_pago + 'T00:00:00');
      const daysUntilDeadline = Math.ceil((deadline - today) / (1000 * 60 * 60 * 24));

      // Ignore expired
      if (daysUntilDeadline < 0) return;

      if (daysUntilDeadline === 0) {
        categories.black.count++;
        categories.black.ocs.push(oc);
      } else if (daysUntilDeadline < 5) {
        categories.red.count++;
        categories.red.ocs.push(oc);
      } else if (daysUntilDeadline < 10) {
        categories.orange.count++;
        categories.orange.ocs.push(oc);
      } else if (daysUntilDeadline < 15) {
        categories.yellow.count++;
        categories.yellow.ocs.push(oc);
      } else {
        categories.green.count++;
        categories.green.ocs.push(oc);
      }
    });

    return categories;
  };

  useEffect(() => {
    const fetchData = async () => {
      if (selectedCompanyIds.length === 0) {
        setDashboardData(null);
        setOcTrafficLight(null);
        setLoading(false);
        return;
      }
      try {
        setLoading(true);

        // Fetch dashboard data
        const dashboardResponse = await axios.get('/api/dashboard', {
          params: {
            empresa_ids: selectedCompanyIds,
            month: selectedMonth,
            year: selectedYear
          },
          paramsSerializer: params => {
            return Object.entries(params)
              .map(([key, value]) =>
                Array.isArray(value) ? value.map(v => `${key}=${encodeURIComponent(v)}`).join('&') : `${key}=${encodeURIComponent(value)}`
              )
              .join('&');
          }
        });
        setDashboardData(dashboardResponse.data);

        // Fetch OCs for traffic light
        try {
          const ocsResponse = await axios.get('/api/ordenes_compra', {
            params: {
              empresa_ids: selectedCompanyIds,
              status_ciclo: 'activo'
            },
            paramsSerializer: params => {
              return Object.entries(params)
                .map(([key, value]) =>
                  Array.isArray(value) ? value.map(v => `${key}=${encodeURIComponent(v)}`).join('&') : `${key}=${encodeURIComponent(value)}`
                )
                .join('&');
            }
          });

          const categories = categorizeOCs(ocsResponse.data || []);
          setOcTrafficLight(categories);
        } catch (ocError) {
          // If user doesn't have permission to view OCs (403), hide traffic light
          if (ocError.response?.status === 403) {
            console.log('User does not have permission to view purchase orders. Hiding traffic light.');
            setOcTrafficLight(null);
          } else {
            // Re-throw other errors to be caught by outer catch
            throw ocError;
          }
        }

        setError('');
      } catch (err) {
        setError('Failed to load dashboard data.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [selectedCompanyIds, selectedMonth, selectedYear]);

  if (loading) {
    return <CircularProgress />;
  }

  if (error) {
    return <Typography color="error">{error}</Typography>;
  }

  if (!dashboardData) {
    return <Typography>Por favor seleccione una empresa para ver el dashboard.</Typography>;
  }

  const { kpis, recent_activity } = dashboardData;

  return (
    <Container maxWidth="xl">
      <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
        Dashboard General
      </Typography>

      {/* KPI Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard title="Requisiciones Pendientes" value={kpis.pending_requisitions} color="#272757" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard title="Requisiciones Activas" value={kpis.active_requisitions} color="#8686ac" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard title="Órdenes de Compra Activas" value={kpis.active_purchase_orders} color="#505081" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <GastoKpiCard
            value={new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(kpis.monthly_spend)}
            color="#0f0e47"
            selectedMonth={selectedMonth}
            selectedYear={selectedYear}
            onMonthChange={setSelectedMonth}
            onYearChange={setSelectedYear}
          />
        </Grid>
      </Grid>

      {/* OC Traffic Light */}
      {ocTrafficLight && (
        <Paper elevation={3} sx={{ mb: 4 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Semáforo de OCs por Fecha Límite de Pago
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2 }}>
                {Object.entries(ocTrafficLight).map(([key, category]) => {
                  // Calculate date ranges for filtering
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  const todayStr = today.toISOString().split('T')[0];

                  let dateFrom, dateTo;

                  if (key === 'black') {
                    // Today only
                    dateFrom = todayStr;
                    dateTo = todayStr;
                  } else if (key === 'red') {
                    // 1-4 days
                    const tomorrow = new Date(today);
                    tomorrow.setDate(tomorrow.getDate() + 1);
                    const in4Days = new Date(today);
                    in4Days.setDate(in4Days.getDate() + 4);
                    dateFrom = tomorrow.toISOString().split('T')[0];
                    dateTo = in4Days.toISOString().split('T')[0];
                  } else if (key === 'orange') {
                    // 5-9 days
                    const in5Days = new Date(today);
                    in5Days.setDate(in5Days.getDate() + 5);
                    const in9Days = new Date(today);
                    in9Days.setDate(in9Days.getDate() + 9);
                    dateFrom = in5Days.toISOString().split('T')[0];
                    dateTo = in9Days.toISOString().split('T')[0];
                  } else if (key === 'yellow') {
                    // 10-14 days
                    const in10Days = new Date(today);
                    in10Days.setDate(in10Days.getDate() + 10);
                    const in14Days = new Date(today);
                    in14Days.setDate(in14Days.getDate() + 14);
                    dateFrom = in10Days.toISOString().split('T')[0];
                    dateTo = in14Days.toISOString().split('T')[0];
                  } else if (key === 'green') {
                    // 15+ days
                    const in15Days = new Date(today);
                    in15Days.setDate(in15Days.getDate() + 15);
                    dateFrom = in15Days.toISOString().split('T')[0];
                    dateTo = null; // No upper limit
                  }

                  return (
                    <Chip
                      key={key}
                      label={`${category.label}: ${category.count}`}
                      sx={{
                        backgroundColor: category.color,
                        color: key === 'yellow' ? '#000' : '#fff',
                        fontWeight: 'bold',
                        fontSize: '0.9rem',
                        padding: '20px 12px',
                        cursor: category.count > 0 ? 'pointer' : 'default',
                        '&:hover': category.count > 0 ? {
                          opacity: 0.8,
                          transform: 'scale(1.05)',
                        } : {}
                      }}
                      onClick={() => {
                        if (category.count > 0) {
                          const params = new URLSearchParams();
                          params.append('fecha_limite_pago_from', dateFrom);
                          if (dateTo) {
                            params.append('fecha_limite_pago_to', dateTo);
                          }
                          navigate(`/ordenes-compra?${params.toString()}`);
                        }
                      }}
                    />
                  );
                })}
              </Box>
            </CardContent>
          </Card>
        </Paper>
      )}

      {/* Recent Activity and Actionable Items */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper elevation={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Actividad Reciente</Typography>
                <List>
                  {recent_activity.map((item, index) => (
                    <React.Fragment key={item.id}>
                      <ListItem 
                        button 
                        onClick={() => {
                          let path;
                          switch (item.type) {
                            case 'requisicion':
                              path = `/requisiciones/${item.id}`;
                              break;
                            case 'orden_compra':
                              path = `/ordenes-compra/${item.id}`;
                              break;
                            case 'solicitud_cotizacion':
                              path = `/solicitudes_cotizacion/${item.id}`;
                              break;
                            default:
                              path = '/';
                          }
                          navigate(path);
                        }}
                      >
                        <ListItemText 
                          primary={item.description}
                          secondary={new Date(item.created_at).toLocaleString()}
                        />
                      </ListItem>
                      {index < recent_activity.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Paper>
        </Grid>
        {/* Placeholder for Actionable Items */}
        <Grid item xs={12} md={6}>
           <Paper elevation={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Mis Tareas</Typography>
                 <List>
                    <ListItem>
                        <ListItemText primary="Requisiciones por Aprobar" secondary="Feature en desarrollo" />
                    </ListItem>
                     <ListItem>
                        <ListItemText primary="Cotizaciones por Revisar" secondary="Feature en desarrollo" />
                    </ListItem>
                 </List>
              </CardContent>
            </Card>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
}

export default Dashboard;
